<script setup>
import DialogDrawer from '@js/components/dialogs/DialogDrawer.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import TextareaField from '@js/components/fields/TextareaField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import { reactive, ref } from 'vue';
import { object, string } from 'yup';

const { tenant, emailingAutomation, callback } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    emailingAutomation: {
        type: Object,
        required: false,
    },
    callback: {
        type: Function,
        required: false,
    },
});

const emailingAutomationEmailExclusionTypes = [
    { label: 'Email', value: 'email' },
    { label: 'Domaine', value: 'domain' },
];

const isOpen = ref(false);

const isSubmitting = ref(false);

const { route } = useRoute();

const form = reactive({
    values: '',
    type: emailingAutomationEmailExclusionTypes[0],
});

const validationSchema = object({
    values: string()
        .required('Les valeurs sont requises')
        .test(
            'at-least-one-value',
            'Veuillez entrer au moins une valeur.',
            (value) => {
                return value.split('\n').some((line) => line.trim() !== '');
            },
        ),
    type: object().required('Le type est requis'),
});

function importAction() {
    isSubmitting.value = true;

    axios
        .post(
            route(
                'front.projectManagement.modules.postImportEmailingAutomationEmailExclusions',
                {
                    tenant: tenant.id,
                },
            ),
            {
                values: form.values
                    .split('\n')
                    .filter((value) => value.trim() !== ''),
                type: form.type.value,
                emailingAutomationId: emailingAutomation
                    ? emailingAutomation.id
                    : null,
            },
        )
        .then(() => {
            isOpen.value = false;

            if (callback) {
                callback();
            }
        })
        .catch((error) => {
            console.error(error);
        })
        .finally(() => {
            isSubmitting.value = false;
        });
}
</script>

<template>
    <DialogDrawer v-model="isOpen">
        <template #trigger>
            <slot name="trigger" />
        </template>

        <template #title> Importer </template>

        <template #full-content>
            <FormRoot
                v-slot="{ errors }"
                :validation-schema="validationSchema"
                :submit="importAction"
            >
                <FormColumn>
                    <FormContent>
                        <FormGrid :columns="1">
                            <TextareaField
                                v-model="form.values"
                                label="Valeurs"
                                field-name="values"
                                field-id="values"
                                legend="Entrez une valeur par ligne"
                                required
                            />
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <FormGrid :columns="1">
                            <ListboxField
                                v-model="form.type"
                                field-id="type"
                                field-name="type"
                                label="Type"
                                :options="emailingAutomationEmailExclusionTypes"
                                required
                            >
                                <template #buttonContent>
                                    {{ form.type.label }}
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.label }}
                                </template>
                            </ListboxField>
                        </FormGrid>
                    </FormContent>
                </FormColumn>

                <FormFooter
                    :is-submitting="isSubmitting"
                    :errors="errors"
                    without-back-button
                    submit-text="Importer"
                    submitting-text="Importation en cours..."
                />
            </FormRoot>
        </template>
    </DialogDrawer>
</template>
